import { css } from '@emotion/css';
import { For, Show } from 'solid-js';
import { type DrillDownGroup, type DrillDownRow, DrillDownSelect } from 'Shared/forms/DrillDownSelect';
import { useSite } from 'Shared/SiteProvider';
import { useVenue } from 'Shared/VenueProvider';
import { VenueSettingsFactory } from 'Shared/VenueSettingsFactory';
import { backendTheme } from 'Shared/backend/Theme';
import { locationClassGroups, locationClassLabel } from 'Shared/backend/styles/locationClasses';
import { ClassContext, settingsParts } from 'Shared/frontend/styles/ruleProperties';
import { LocationPathStep } from 'Shared/model/venueSettings';


/* Where a style rule applies. Kept here rather than taken off one of the schemas that store it -
   customStyleRule's 'location' and a style tag's 'selector' param are the two, and they are
   deliberately independent of each other. */
export type StyleLocation = 'self'|'class'|'path';

/* Class rows share the panel with the self row, so they carry a prefix rather than risking a
   class named 'self'. */
const classKeyPrefix = 'class:';

/* The row in an add panel that matches on nothing but the relation - any child, or anything
   below. Every other row there is a class. */
const anyKey = 'any';


const wrapStyle = css({
	display:       'flex',
	flexDirection: 'column',
	alignItems:    'flex-start',
	gap:           '0.4em',
});

const pathStyle = css({
	display:       'flex',
	flexDirection: 'column',
	alignItems:    'flex-start',
	gap:           '0.25em',
	paddingLeft:   '0.8em',
	borderLeft:    'solid 1px #cccccc',
});

const stepStyle = css({
	display:    'flex',
	alignItems: 'baseline',
	gap:        '0.5em',
	fontSize:   '0.85em',
	color:      backendTheme().palette.label,
});

const addStyle = css({
	display:   'flex',
	gap:       '1em',
	marginTop: '0.1em',
});

const trashStyle = () => css(backendTheme().a, {
	color:     '#666',
	'&:hover': {color: backendTheme().palette.error},
});

const noteStyle = () => css({
	color:     backendTheme().palette.label,
	fontSize:  '0.85em',
	fontStyle: 'italic',
});


/*
	Where one style rule applies: self - the component the style is on - a class, which matches
	anything at or below the styled component that carries it, or a path, which is the general case
	the other two are shorthands for. The classes on offer are grouped by where they come from; see
	locationClasses.ts.

	Used by both the Style Builder's rules (StyleBuilderPage) and the standard styles that let the
	venue point them somewhere (Selector.tsx).

	A path is a list of steps built here and stored on the rule - see locationPathStep in
	venueSettings.ts and pathLocation() in locationMatches.ts, which turns it into an expression.
	Steps are added at the end and removed one at a time; each is a class or a wildcard, sitting
	either directly under the previous step or anywhere below it, so the two add controls between
	them offer the four things a step can be.

	Nothing is held here: picking a location, adding a step and removing one all hand the whole
	choice back through the one 'choose', and the caller stores it. 'locationClass' belongs to the
	'class' location and 'locationPath' to 'path'; each is passed on every call, and it is the caller
	that drops the one its new location has no use for.

	'context' says which list of things the rule can point at. The two style contexts offer Self, a
	Path and the classes, 'styleBuilder' adding the embedded-html elements - bold text and the like,
	which only a Style Builder rule can resolve (see embeddedElements in ruleProperties.ts). They are
	never offered as a path step: they name no component and are resolved as a nested css selector, so
	they only mean anything as the whole location.

	A settings context - a nav node, the sub-nav - offers a flat list of that node's parts instead and
	nothing else. There is no Self, because a part already says whether it dresses the node itself,
	and no Path, because nothing under the node is on a style path to walk.

	'plain' drops the box around the trigger so it reads as plain text - the location is nearly always
	the same and should not draw the eye. The path sub-editor is unaffected.

	'prefix' is the caption shown inside the trigger ahead of the location ('Apply to'), so the whole
	line is one clickable target rather than a label sitting beside it. '' leaves the caption to the
	caller's own wrapper.

	'panelAlign' is the edge the dropdown lines up with: 'right' for a trigger that sits at the right
	of its row, 'left' otherwise.
*/
export function LocationBuilder(P:{location:StyleLocation, locationClass:string,
	locationPath:LocationPathStep[], context:ClassContext, plain:boolean, prefix:string,
	panelAlign:'left'|'right',
	choose:(location:StyleLocation, locationClass:string, locationPath:LocationPathStep[]) => void})
{
	const siteTheme = (new VenueSettingsFactory())
		.createSettings(useSite().key,useVenue().settings).siteTheme();

	const venueClasses = (): string[] => siteTheme.classes ?? [];

	const parts = () => settingsParts[P.context];

	const label = () => {
		if (P.location == 'class')
			return locationClassLabel(venueClasses(),P.context,P.locationClass);
		/* The steps are listed under the trigger, so the trigger itself just names the mode. */
		if (P.location == 'path')
			return 'Path';

		/* A settings context has no 'self' to offer, so a rule sitting on one is a rule just added and
		   not yet pointed at a part. */
		return parts()==undefined ? 'Self' : 'Choose a part';
	};

	/* A settings context's parts, as the one flat list the picker offers. */
	const partRows = (): DrillDownRow[] => (parts() ?? []).map(entry => ({
		key:     classKeyPrefix + entry.part,
		label:   entry.label,
		note:    '',
		current: P.location=='class' && P.locationClass==entry.part,
	}));

	const selfRows = (): DrillDownRow[] => [{
		key:     'self',
		label:   'Self',
		note:    '',
		current: P.location == 'self',
	}];

	const pathRows = (): DrillDownRow[] => [{
		key:     'path',
		label:   'Path',
		note:    'Built step by step',
		current: P.location == 'path',
	}];

	const chooseLocation = (key:string) => {
		if (key.startsWith(classKeyPrefix))
			P.choose('class',key.slice(classKeyPrefix.length),copySteps(P.locationPath));
		else
			P.choose(key as StyleLocation,'',copySteps(P.locationPath));
	};

	const addStep = (relation:LocationPathStep['relation'], key:string) => {
		const aClass = key.startsWith(classKeyPrefix) ? key.slice(classKeyPrefix.length) : '';
		P.choose('path','',[...copySteps(P.locationPath),{relation,aClass}]);
	};

	const removeStep = (index:number) =>
		P.choose('path','',copySteps(P.locationPath.filter((step,i) => i!=index)));

	return (
		<div class={wrapStyle}>
			<DrillDownSelect
				prefix={P.prefix}
				label={label()}
				sections={parts()!=undefined ? [partRows()] : [selfRows(),pathRows()]}
				groups={parts()!=undefined ? []
					: classGroups(venueClasses(),P.context, P.location=='class' ? P.locationClass : '')}
				compact={false}
				plain={P.plain}
				panelAlign={P.panelAlign}
				choose={chooseLocation}
			/>

			<Show when={P.location == 'path'}>
				<div class={pathStyle}>
					<For each={P.locationPath}
						fallback={<div class={noteStyle()}>
							Add the first step of the path.
						</div>}
					>{(step,index) =>
						<div class={stepStyle}>
							<span>{stepLabel(venueClasses(),step)}</span>
							<a class={trashStyle()} title='Remove step' onClick={() => removeStep(index())}>
								<i class='fas fa-trash' />
							</a>
						</div>
					}</For>

					<div class={addStyle}>
						<DrillDownSelect
							prefix=''
							label='Add child'
							sections={[anyRows('child')]}
							groups={classGroups(venueClasses(),'selector','')}
							compact={false}
							plain={true}
							panelAlign='left'
							choose={key => addStep('child',key)}
						/>

						<DrillDownSelect
							prefix=''
							label='Add descendant'
							sections={[anyRows('descendant')]}
							groups={classGroups(venueClasses(),'selector','')}
							compact={false}
							plain={true}
							panelAlign='left'
							choose={key => addStep('descendant',key)}
						/>
					</div>
				</div>
			</Show>
		</div>
	);
}


/* Plain copies of the steps. What comes in off a store is a proxy over the very array the caller
   then reconciles the new list onto, and reconcile mutates in place. */
function copySteps(path:LocationPathStep[]): LocationPathStep[]
{
	return path.map(step => ({...step}));
}


/* The class groups as drill-down categories. 'chosen' is the class shown in bold - '' where
   nothing is being chosen from, as in the add panels, which start a new step every time. */
function classGroups(venueClasses:string[], context:ClassContext, chosen:string): DrillDownGroup[]
{
	return locationClassGroups(venueClasses,context)
		.filter(group => group.classes.length > 0)
		.map(group => ({
			key:       group.name,
			label:     `Class: ${group.label}`,
			emptyNote: 'No classes here.',
			rows:      group.classes.map(entry => ({
				key:     classKeyPrefix + entry.name,
				label:   entry.label,
				note:    '',
				current: chosen == entry.name,
			})),
		}));
}


/* The row an add panel offers ahead of the classes: the step that filters on nothing. */
function anyRows(relation:LocationPathStep['relation']): DrillDownRow[]
{
	return [{
		key: anyKey,
		label: 'Any',
		note: '',
		current: false,
	}];
}


function stepLabel(venueClasses:string[], step:LocationPathStep): string
{
	if (step.aClass == '')
		return step.relation=='child' ? 'Any child' : 'Any descendant';

	return step.relation=='child'
		? `Child: ${locationClassLabel(venueClasses,'styleBuilder',step.aClass)}`
		: `Descendant: ${locationClassLabel(venueClasses,'styleBuilder',step.aClass)}`;
}
